import React, { useState } from "react";
import { PatientProfile } from "./types";
import ClinicIcon from "../common/ClinicIcon";
import Button from "../common/Button";

interface PatientEditModalProps {
    patient: PatientProfile;
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Partial<PatientProfile>) => Promise<void>;
}

export default function PatientEditModal({ patient, isOpen, onClose, onSave }: PatientEditModalProps) {
    const [formData, setFormData] = useState<Partial<PatientProfile>>({
        name: patient.name || "",
        age: patient.age,
        gender: patient.gender || "Male",
        mobile: patient.mobile || patient.phone || "",
        area: patient.area || "",
        medical_alerts: patient.medical_alerts || "",
        carestream_id: patient.carestream_id || "",
    });
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } catch (err) {
            console.error("Failed to save patient", err);
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: "100%",
        padding: "10px 14px",
        borderRadius: "8px",
        border: "1px solid #cbd5e1",
        fontSize: "0.95rem",
        boxSizing: "border-box" as const,
        marginTop: "4px",
    };

    const labelStyle = {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: "#475569",
        display: "block",
    };

    return (
        <div className="mod-modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
        }}>
            <div className="mod-modal-content" style={{
                background: '#fff',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '500px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                overflow: 'hidden'
            }}>
                <header style={{
                    padding: '20px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#f8fafc'
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>Edit Patient Details</h3>
                    <button onClick={onClose} style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#64748b',
                        padding: '4px'
                    }}>
                        <ClinicIcon name="close" size={20} />
                    </button>
                </header>

                <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={labelStyle}>Full Name</label>
                        <input name="name" value={formData.name} onChange={handleChange} style={inputStyle} required />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                            <label style={labelStyle}>Age</label>
                            <input name="age" type="number" value={formData.age || ""} onChange={handleChange} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Gender</label>
                            <select name="gender" value={formData.gender} onChange={handleChange} style={inputStyle}>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                            <label style={labelStyle}>Area / City</label>
                            <input name="area" value={formData.area || ""} onChange={handleChange} style={inputStyle} />
                        </div>
                        <div>
                            <label style={{ ...labelStyle, color: '#0284c7' }}>Carestream Sync ID</label>
                            <input 
                                name="carestream_id" 
                                value={formData.carestream_id || ""} 
                                onChange={handleChange} 
                                style={{ ...inputStyle, borderColor: '#38bdf8', background: '#f0f9ff' }} 
                                placeholder="e.g. SE0081"
                            />
                        </div>
                    </div>

                    <div>
                        <label style={{ ...labelStyle, color: '#dc2626' }}>Medical Alerts / Allergies</label>
                        <textarea
                            name="medical_alerts"
                            value={formData.medical_alerts || ""}
                            onChange={handleChange}
                            rows={3}
                            style={{ ...inputStyle, background: '#fef2f2', borderColor: '#fca5a5' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                        <Button type="button" variant="outline" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
                        <Button type="submit" variant="primary" isLoading={loading} style={{ flex: 1 }}>Save Changes</Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
